// SPDX-License-Identifier: AGPL-3.0-or-later
// Copyright (C) 2026 Ben Richardson — https://benrichardson.dev
// See ADDITIONAL-TERMS.md for the section 7(b) attribution requirement.
//
// Clatter — the menu, the two sheets of copy, and the results screen. Markup only; main.ts wires it.

import { escapeHtml } from './dom';
import { MODE_IDS, modeOf } from './modes';
import { TIERS, TIER_IDS } from './bot';
import { TRACK_GLYPH, TRACK_NAME, type Breakdown } from './rules';
import { dieHtml } from './render';

export const ATTRIB =
  'Built by <a href="https://benrichardson.dev/" target="_blank" rel="noopener">benrichardson.dev</a> · ' +
  '<a href="https://lab.benrichardson.dev" target="_blank" rel="noopener">more games, tools &amp; sites</a>';

const LOGO = `<svg viewBox="0 0 64 64" aria-hidden="true" focusable="false">
  <rect x="6" y="6" width="38" height="38" rx="9" fill="#F7F4EC"/>
  <circle cx="16" cy="16" r="4" fill="#14151C"/><circle cx="25" cy="25" r="4" fill="#14151C"/><circle cx="34" cy="34" r="4" fill="#14151C"/>
  <rect x="26" y="26" width="32" height="32" rx="8" fill="#F0B429"/>
  <circle cx="35" cy="35" r="3.4" fill="#14151C"/><circle cx="49" cy="49" r="3.4" fill="#14151C"/><circle cx="49" cy="35" r="3.4" fill="#14151C"/><circle cx="35" cy="49" r="3.4" fill="#14151C"/>
</svg>`;

export interface MenuOpts {
  modeId: string;
  tierId: string;
  players: number;
  best: number;
  muted: boolean;
}

export function menuHtml(o: MenuOpts): string {
  const mode = modeOf(o.modeId);
  return `<section class="screen menu">
    <div class="brand">
      <div class="logo">${LOGO}</div>
      <h1>Clatter</h1>
      <p class="tagline">Roll six dice, take one — and every smaller die goes to everybody else.</p>
    </div>

    <div>
      <p class="field-label">Mode</p>
      <div class="chips">${MODE_IDS.map(
        (id) =>
          `<button class="chip${id === o.modeId ? ' on' : ''}" data-mode="${id}" aria-pressed="${id === o.modeId}">${escapeHtml(
            modeOf(id).name,
          )}</button>`,
      ).join('')}</div>
      <p class="mode-blurb">${escapeHtml(mode.blurb)}</p>
      ${o.best > 0 ? `<p class="mode-best">Your best in ${escapeHtml(mode.name)}: <strong>${o.best}</strong></p>` : ''}
    </div>

    <div>
      <p class="field-label">Opponents</p>
      <div class="chips">${[1, 2, 3]
        .map(
          (k) =>
            `<button class="chip${k === o.players - 1 ? ' on' : ''}" data-players="${k + 1}" aria-pressed="${k === o.players - 1}">${k} bot${
              k === 1 ? '' : 's'
            }</button>`,
        )
        .join('')}</div>
      <div class="chips">${TIER_IDS.map(
        (id) =>
          `<button class="chip${id === o.tierId ? ' on' : ''}" data-tier="${id}" aria-pressed="${id === o.tierId}">${escapeHtml(
            TIERS[id].name,
          )}</button>`,
      ).join('')}</div>
    </div>

    <div class="menu-actions">
      <button class="btn primary" id="playSolo">Play</button>
      <button class="btn" id="playFriends">Play with friends</button>
    </div>
    <div class="menu-links">
      <button class="linkbtn" id="help">How to play</button>
      <button class="linkbtn" id="about">About</button>
      <button class="linkbtn" id="mute">${o.muted ? 'Sound off' : 'Sound on'}</button>
    </div>
  </section>`;
}

export function helpHtml(): string {
  const d = (f: number): string => dieHtml(f, { small: true });
  return `<h2>How to play</h2>
    <button class="sheet-close x" aria-label="Close">✕</button>
    <ol class="help-list">
      <li>On your turn, <strong>take one die</strong> from the roll and write it into one of your four tracks.</li>
      <li><strong>Every die smaller than the one you took spills</strong> — and everyone else takes one from it. Grab the ${d(
        6,
      )} you need and you hand the table five dice; take the ${d(1)} and they get nothing.</li>
      <li>If there is nothing you can use, you bank a <strong>shim</strong> ◇ — spend it later to nudge a die up or down by one.</li>
      <li>Marked cells set off <strong>free marks in the other tracks</strong>. One good die can cascade three deep. Highest total wins.</li>
    </ol>
    <h3>The four tracks</h3>
    <ul class="help-list small">
      <li><b>${TRACK_GLYPH[0]} ${TRACK_NAME[0]}</b> — each die higher than the last. Starting low buys you the whole climb.</li>
      <li><b>${TRACK_GLYPH[1]} ${TRACK_NAME[1]}</b> — a die goes in its own column, two deep. Every face has a home here, until it hasn't.</li>
      <li><b>${TRACK_GLYPH[2]} ${TRACK_NAME[2]}</b> — at least the printed number, left to right. Where the big dice go.</li>
      <li><b>${TRACK_GLYPH[3]} ${TRACK_NAME[3]}</b> — two dice that make seven. Open a pair with a ${d(2)} and it wants a ${d(5)} forever.</li>
    </ul>
    <p class="help-note">Desktop: <kbd>1</kbd>–<kbd>6</kbd> pick a die, <kbd>A</kbd> <kbd>T</kbd> <kbd>R</kbd> <kbd>W</kbd> choose a track,
      <kbd>S</kbd> arms a shim, <kbd>Esc</kbd> goes back, <kbd>P</kbd> pauses.</p>`;
}

export function aboutHtml(): string {
  return `<h2>About Clatter</h2>
    <button class="sheet-close x" aria-label="Close">✕</button>
    <p>A dice game about generosity you cannot avoid. Everything you want is something somebody else gets a piece of.</p>
    <p class="help-note">Play solo against bots, or open a room and send a friend the link — up to four of you, on one shared roll.</p>
    <h3>Privacy</h3>
    <p class="help-note">No cookies, no accounts, no tracking beyond anonymous, cookie-less page-view counts.
      Multiplayer is <strong>peer-to-peer</strong>: your browsers talk directly to each other, and a public
      signalling relay is used only to introduce them. No game state ever reaches a server, because there is no server.
      Connecting to a peer exchanges IP addresses with them, the same as any direct connection.</p>
    <h3>Fair dice</h3>
    <p class="help-note">Every roll is generated from the round's shared seed, so all players see the identical dice and
      nothing is rolled behind anyone's back.</p>
    <p class="about-attrib">${ATTRIB}</p>`;
}

export interface ResultRow {
  seat: number;
  name: string;
  isSelf: boolean;
  isBot: boolean;
  rank: number;
  breakdown: Breakdown;
  written: number;
  chain: number;
  passed: number;
  left: boolean;
  /** Tracks dropped by a best-of scoring mode. */
  dropped: number[];
}

export interface ResultsOpts {
  rows: ResultRow[];
  modeName: string;
  round: number;
  tally: number[];
  names: string[];
  multi: boolean;
  fault: string | null;
  best: number;
  /** One line on the biggest thing the local player passed up. */
  missed: string;
}

export function resultsHtml(o: ResultsOpts): string {
  const top = o.rows.filter((r) => r.rank === 1);
  const meWon = top.some((r) => r.isSelf);
  const head = o.fault
    ? 'Round stopped'
    : top.length > 1
      ? 'A dead heat'
      : meWon
        ? 'You win'
        : `${top[0]?.name ?? 'Nobody'} wins`;

  const cell = (label: string, v: number | string): string => `<div><dt>${label}</dt><dd>${v}</dd></div>`;

  const rows = o.rows
    .map((r) => {
      const b = r.breakdown;
      const track = (i: number, v: number): string =>
        `<div class="${r.dropped.includes(i) ? 'dropped' : ''}"><dt>${TRACK_GLYPH[i]} ${TRACK_NAME[i]}</dt><dd>${
          r.dropped.includes(i) ? '—' : v
        }</dd></div>`;
      return `<div class="rrow${r.rank === 1 ? ' won' : ''}">
        <div class="rname"><span class="rtok s${r.seat}"></span>${escapeHtml(r.name)}${r.isSelf ? ' <em>(you)</em>' : ''}${
          r.isBot ? ' <em>(bot)</em>' : ''
        }${r.left ? ' <em>(left)</em>' : ''}<span class="rtotal">${b.total}</span></div>
        <p class="rverdict">${r.rank === 1 ? 'Winner' : `#${r.rank}`} · ${r.written} dice written · ${r.chain} free marks${
          r.passed ? ` · ${r.passed} turns with nothing to take` : ''
        }</p>
        <dl class="rstats">
          ${track(0, b.ladder)}${track(1, b.tally)}${track(2, b.run)}${track(3, b.weights)}
          ${cell('Bonus', b.pts)}${b.tips ? cell('Tips', b.tips) : ''}${cell('Shims', b.shims)}
        </dl>
      </div>`;
    })
    .join('');

  const tally =
    o.multi && o.tally.some((v) => v > 0)
      ? `<p class="results-tally">Match: ${o.tally.map((v, i) => `${escapeHtml(o.names[i] ?? `P${i + 1}`)} ${v}`).join(' · ')}</p>`
      : '';

  return `<div class="results-head">
      <h2>${escapeHtml(head)}</h2>
      <p class="results-sub">${escapeHtml(o.modeName)} · round ${o.round}${o.best > 0 ? ` · your best ${o.best}` : ''}</p>
      ${tally}
      ${o.missed ? `<p class="results-missed">${escapeHtml(o.missed)}</p>` : ''}
    </div>
    <div class="rrows">${rows}</div>`;
}
